import { inject, Injectable } from '@angular/core';
import { type IdToken, type RedirectLoginResult } from '@auth0/auth0-spa-js';
import { signInWithCustomToken } from 'firebase/auth';
import { CookieService } from 'ngx-cookie-service';
import { from, NEVER, type Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { appConstants } from '../../app.constants';
import { ApiService } from '../api/api.service';
import { FirebaseProvider } from '../services';
import { AUTH0_CLIENT } from './auth0-client.token';

@Injectable({
  providedIn: 'root'
})
export class Auth0Service {
  readonly #api = inject(ApiService);
  readonly #client = inject(AUTH0_CLIENT);
  readonly #cookie = inject(CookieService);
  readonly #firebaseProvider = inject(FirebaseProvider);

  #getTokenSilently(cacheMode: 'on' | 'off' = 'on'): Observable<string> {
    return from(this.#client.getTokenSilently({ cacheMode })).pipe(
      switchMap(() => from(this.#client.getIdTokenClaims())),
      map((claims: IdToken | undefined) => claims?.__raw ?? '')
    );
  }

  get #handleRedirectCallback$(): Observable<RedirectLoginResult> {
    return from(this.#client.handleRedirectCallback()).pipe(map(result => result as RedirectLoginResult));
  }

  get #isAuthenticated$(): Observable<boolean> {
    return from(this.#client.isAuthenticated());
  }

  get isAuthenticated$(): Observable<boolean> {
    return this.#isAuthenticated$;
  }

  public redirectPath: string | null = null;

  #firebaseToken: string | null = null;

  public checkCookies(): Observable<string> {
    return this.isAuthenticated$.pipe(
      switchMap(isAuthenticated => {
        if (!isAuthenticated) {
          return of('');
        }

        return of(this.#cookie.get(appConstants.cookies.firebaseToken)).pipe(
          /** Check if firebase token is present or has expired */
          switchMap(fbToken => {
            const payload: Record<string, unknown> = fbToken ? JSON.parse(atob(fbToken.split('.')[1])) : {};
            const isExpired = typeof payload['exp'] === 'number' && payload['exp'] < Date.now() / 1000;
            if (!fbToken || isExpired) {
              return this.#getFirebaseToken();
            }
            this.#firebaseToken = fbToken;
            return of(void 0);
          }),
          switchMap(() => this.#loginToFirebase())
        );
      })
    );
  }

  public checkSession(): Observable<string> {
    return this.#establishSession().pipe(
      switchMap(() => this.#getFirebaseToken()),
      switchMap(() => this.#loginToFirebase())
    );
  }

  public login(appState?: string): Observable<void> {
    return from(
      this.#client.loginWithRedirect({
        appState: appState ?? window.location.href.replace(window.location.origin, ''),
        authorizationParams: {
          redirect_uri: window.location.origin
        }
      })
    );
  }

  public logout(): Observable<void> {
    this.#clearCookies();
    return from(
      this.#client.logout({
        logoutParams: {
          returnTo: window.location.origin
        }
      })
    );
  }

  public getTokenSilently(forceRefresh = false): Observable<string> {
    return this.#getTokenSilently(forceRefresh ? 'off' : 'on').pipe(
      catchError((error: unknown) => {
        const isLoginRequired =
          typeof error === 'object' &&
          error !== null &&
          'error' in error &&
          (error as Record<string, unknown>).error === 'login_required';

        if (isLoginRequired) {
          return of('');
        }

        return this.logout().pipe(switchMap(() => NEVER));
      })
    );
  }

  #establishSession(): Observable<void> {
    const url = new URL(window.location.href);

    if (url.searchParams.has('error') && url.searchParams.has('state')) {
      const error = url.searchParams.get('error');
      const errorDescription = url.searchParams.get('error_description');
      console.error(`[Auth] callback error: ${error} - ${errorDescription}`);
      this.#cleanCallbackParams(url);
      sessionStorage.setItem('auth0_error', `${error}: ${errorDescription}`);
      return this.logout().pipe(
        switchMap(() => throwError(() => new Error(`Auth0 login failed: ${error} - ${errorDescription}`)))
      );
    }

    if (url.searchParams.has('code') && url.searchParams.has('state')) {
      return this.#handleSuccessCallback(url);
    }

    return this.#verifyOrRefreshSession();
  }

  #handleSuccessCallback(url: URL): Observable<void> {
    return this.#handleRedirectCallback$.pipe(
      switchMap(({ appState }) => {
        this.#cleanCallbackParams(url);
        if (appState && appState !== '/') {
          this.redirectPath = appState;
        }
        return of(void 0);
      })
    );
  }

  #verifyOrRefreshSession(): Observable<void> {
    const authError = sessionStorage.getItem('auth0_error');
    if (authError) {
      sessionStorage.removeItem('auth0_error');
      return throwError(() => new Error(`Auth0 login failed: ${authError}`));
    }

    return this.#isAuthenticated$.pipe(
      switchMap(isAuthenticated =>
        isAuthenticated
          ? of(void 0)
          : this.#getTokenSilently().pipe(
              map(() => undefined),
              catchError(() => this.#redirectToLogin())
            )
      )
    );
  }

  #redirectToLogin(appState?: string): Observable<never> {
    return this.login(appState).pipe(switchMap(() => NEVER));
  }

  #cleanCallbackParams(url: URL): void {
    ['state', 'code', 'error', 'error_description'].forEach(p => url.searchParams.delete(p));
    window.history.replaceState(null, '', url.href);
  }

  #getFirebaseToken(): Observable<void> {
    return this.#api.core.getFireBaseToken().pipe(
      map(token => {
        this.#firebaseToken = token;
        this.#setCookie(appConstants.cookies.firebaseToken, token);
      })
    );
  }

  #loginToFirebase(): Observable<string> {
    if (!this.#firebaseToken) {
      return throwError(() => new Error('Firebase token missing'));
    }
    return from(signInWithCustomToken(this.#firebaseProvider.auth, this.#firebaseToken)).pipe(
      map(credentials => {
        if (!credentials.user) {
          throw new Error('No user in firebase credentials');
        }
        return credentials.user.uid;
      })
    );
  }

  #setCookie(name: string, value: string): void {
    let expires: Date | undefined;
    try {
      const payload: Record<string, unknown> = JSON.parse(atob(value.split('.')[1]));
      if (typeof payload['exp'] === 'number') {
        expires = new Date(payload['exp'] * 1000);
      }
    } catch {
      expires = undefined;
    }
    this.#cookie.set(name, value, expires, '/', environment.cookies.domain);
  }

  #clearCookies(): void {
    this.#cookie.deleteAll('/', environment.cookies.domain);
  }
}
